import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tag } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function normalizeCode(value) {
  return (value || "").trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 40);
}

function normalizeCouponResponse(data, code) {
  const policy = data?.policy || {};
  return {
    valid: data?.valid === true,
    kind: data?.kind || "coupon",
    code: data?.code || code,
    discount_type: data?.discount_type || "percentage",
    discount_value: Number(data?.discount_value || 0),
    discount_amount: Number(data?.discount_amount || 0),
    final_total: data?.final_total == null ? null : Number(data.final_total),
    product_ids: data?.product_ids || policy.product_ids || [],
    pickup_only: data?.pickup_only === true || policy.pickup_only === true,
    online_only: data?.online_only === true || policy.online_only === true,
    max_quantity_per_order: Number(data?.max_quantity_per_order || policy.max_quantity_per_order || 0),
  };
}

export default function CheckoutCouponSection({
  tc, coupon, onCouponChange, restaurant, restaurantId, customer, apiUrl,
  cart = [], cartTotal = 0, orderType = "delivery", paymentMethod = "online",
}) {
  const { t } = useTranslation();
  const [validating, setValidating] = useState(false);
  const autoAppliedRef = useRef("");
  const linkedCode = (() => {
    try { return normalizeCode(new URLSearchParams(window.location.search).get("coupon")); }
    catch { return ""; }
  })();

  const validateCode = useCallback(async ({ silent = false } = {}) => {
    const code = normalizeCode(coupon.code);
    if (!code || validating) return;
    setValidating(true);
    onCouponChange.code(code);
    onCouponChange.discount(null);
    try {
      const items = cart.map(item => ({
        product_id: item.id || item.product_id,
        quantity: item.quantity || 1,
      }));
      const rawSubtotal = cart.reduce(
        (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
        0,
      );
      let validated;
      try {
        const res = await axios.post(`${apiUrl}/marketing/validate-coupon`, {
          code,
          restaurant_id: restaurant?.id || restaurantId,
          subtotal: rawSubtotal,
          items,
          order_type: orderType,
          payment_method: paymentMethod,
          // Personal coupons (Kaiso Prime) are bound to a customer; sending the
          // phone lets the preview reject a code that is not theirs.
          customer_phone: customer?.phone || "",
        });
        validated = normalizeCouponResponse(res.data, code);
        if (!validated.valid) throw new Error("invalid_coupon_response");
      } catch (couponError) {
        // A 404 means this is not a Marketing coupon. Preserve the legacy
        // referral-code flow; expired/exhausted coupons must not fall through.
        if (couponError?.response?.status !== 404) throw couponError;
        const referral = await axios.post(`${apiUrl}/referral/validate-code`, {
          code,
          restaurant_id: restaurant?.id || restaurantId,
          phone: customer.phone,
        });
        const pct = Number(referral.data?.discount_percent || 0);
        if (!referral.data?.valid || pct <= 0) throw new Error("invalid_referral_response");
        const amount = Math.round(Number(cartTotal || 0) * pct) / 100;
        validated = {
          valid: true,
          kind: "referral",
          code,
          discount_type: "percentage",
          discount_value: pct,
          // Referral preview only. The order backend remains authoritative.
          discount_amount: amount,
          final_total: Math.max(0, Number(cartTotal || 0) - amount),
          product_ids: [], pickup_only: false, online_only: false, max_quantity_per_order: 0,
        };
      }
      onCouponChange.discount(validated);
      if (!silent) toast.success(t("checkout.couponApplied", { pct: validated.discount_value }));
    } catch (err) {
      onCouponChange.discount(null);
      toast.error(err?.response?.data?.detail || t("checkout.couponInvalid"));
    } finally {
      setValidating(false);
    }
  }, [apiUrl, cart, cartTotal, coupon.code, customer.phone, onCouponChange, orderType, paymentMethod, restaurant?.id, restaurantId, t, validating]);

  useEffect(() => {
    const code = normalizeCode(coupon.code);
    if (!code || code !== linkedCode || coupon.discount || autoAppliedRef.current === code) return;
    autoAppliedRef.current = code;
    validateCode({ silent: true });
  }, [coupon.code, coupon.discount, linkedCode, validateCode]);

  const discount = coupon.discount;
  const discountLabel = discount?.discount_type === "fixed"
    ? `€${discount.discount_value.toFixed(2)}`
    : `${discount?.discount_value || 0}%`;

  return (
    <div>
      <Label className="text-xs font-semibold uppercase" style={{ color: tc.primary }}>{t("checkout.couponLabel")}</Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          value={coupon.code}
          onChange={e => {
            autoAppliedRef.current = "";
            onCouponChange.code(e.target.value.toUpperCase());
            onCouponChange.discount(null);
          }}
          placeholder={t("checkout.couponPlaceholder")}
          className="bg-gray-50"
          data-testid="coupon-input"
        />
        <Button
          variant="outline" size="sm" className="shrink-0"
          disabled={validating}
          data-testid="validate-coupon-btn"
          onClick={() => validateCode()}
        ><Tag className="w-4 h-4" /></Button>
      </div>
      {discount?.valid && (
        <div className="mt-1 space-y-0.5" data-testid="coupon-discount-msg">
          <p className="text-xs font-bold" style={{ color: tc.primary }}>
            Cupón {discount.code}: {discountLabel} aplicado
          </p>
          {discount.pickup_only && <p className="text-xs text-gray-500">Oferta exclusiva para recogida.</p>}
          {discount.online_only && <p className="text-xs text-gray-500">Solo con pago online.</p>}
        </div>
      )}
    </div>
  );
}
